export const normalizeLabelTitles = (labels = []) => [
  ...new Set(
    (Array.isArray(labels) ? labels : [])
      .map(label => String(label || '').trim())
      .filter(Boolean)
  ),
];

export const labelSignature = labels =>
  normalizeLabelTitles(labels).slice().sort().join('\u0000');

const hasPendingRecord = (pending, id) =>
  Object.prototype.hasOwnProperty.call(pending, Number(id));

export const updatingLabelTitles = (saved, pending) => {
  if (!pending) return [];

  const savedSet = new Set(saved || []);
  const pendingSet = new Set(pending);
  return [...new Set([...savedSet, ...pendingSet])].filter(
    title => savedSet.has(title) !== pendingSet.has(title)
  );
};

/**
 * One in-flight label update per record.
 * A repeated click with the same titles reuses that request.
 * A different selection waits and is sent once, after the current request finishes.
 */
export function createLabelRequestGate() {
  const inflight = new Map();
  const queued = new Map();

  function isLatest(id, labels) {
    const next = queued.get(id);
    if (!next) return true;
    return labelSignature(next) === labelSignature(labels);
  }

  function run(id, labels, perform) {
    const numericId = Number(id);
    const signature = labelSignature(labels);
    const current = inflight.get(numericId);

    if (current) {
      if (current.signature === signature) return current.promise;

      queued.set(numericId, labels);
      return current.promise
        .catch(() => undefined)
        .then(() => {
          const next = inflight.get(numericId);
          return next ? next.promise : undefined;
        });
    }

    const promise = (async () => {
      try {
        return await perform(labels, () => isLatest(numericId, labels));
      } finally {
        inflight.delete(numericId);
        const next = queued.get(numericId);
        queued.delete(numericId);
        if (next && labelSignature(next) !== signature) {
          run(numericId, next, perform).catch(() => undefined);
        }
      }
    })();

    inflight.set(numericId, { signature, promise });
    return promise;
  }

  return { run };
}

export const labelRecordHelpers = {
  hasPendingRecord,
};
